// ── News (Serper) integration settings ───────────────────────────────────────
//
// The source behind News Monitor: Google News through Serper.dev
// (POST https://google.serper.dev/news, header X-API-KEY). Replaces GDELT,
// which refused Azure's shared outbound IP.
//
// THE KEY IS WRITE-ONLY HERE. What is typed goes to Key Vault as
// 'serper-apikey' (must match SECRET_APIKEY in api/shared/newsSearch.js) and is
// never read back; the page shows set / not set. Blank = leave it alone.
//
// Settings live in dbo.integrations ('serper'), read on every scan:
//   isactive              off = the weekly search does nothing (the loop ends at once)
//   lookbackdays          a name's first search reaches back this far
//   duehours              a name is searched again once this many hours have passed
//   headlinewordrequired  severity is graded on the headline only, not the snippet
//
// The trouble words (dbo.NewsKeywords) are edited here too, since they decide
// what is filed. Saved through saveNewsSubject { keyword }; News Monitor shows
// a count and links here.
//
// The URL is pinned in code, not typed here: a mistyped host would send
// borrower names to somebody else.
import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { Btn, Card, ErrorMsg, Field, SectionHeader, Spinner } from "../../../shared/ui";
import { fmtDate } from "../../../shared/format";
import { useApp } from "../../../shared/appContext";

const PROVIDER = "serper";
const SECRET_APIKEY = "serper-apikey";                    // must match api/shared/newsSearch.js
const BASE_URL = "https://google.serper.dev/news";        // must match api/shared/newsSearch.js
const DEFAULTS = { lookbackdays: 30, duehours: 160, headlinewordrequired: false };

const inp = { width: "100%", boxSizing: "border-box", padding: "8px 10px", fontSize: 13, borderRadius: 6, border: "1px solid #ccc" };
const Row = ({ k, children }) => (
  <div style={{ display: "grid", gridTemplateColumns: "190px minmax(0, 1fr)", gap: 12, padding: "7px 0",
                borderTop: "0.5px solid var(--color-border-tertiary)", fontSize: 13 }}>
    <span style={{ color: "var(--color-text-secondary)" }}>{k}</span><span>{children}</span>
  </div>
);
const mono = { fontFamily: "var(--font-mono)", fontSize: 12.5, wordBreak: "break-all" };
const hint = { fontSize: 11.5, color: "var(--color-text-secondary)", marginTop: -8, marginBottom: 12 };
const chip = ok => ({ fontSize: 11.5, padding: "2px 8px", borderRadius: 4, fontWeight: 600,
                      background: ok ? "#E1F5EE" : "#FBE4DD", color: ok ? "#0F6E56" : "#993C1D" });

export const IntegrationSerperPage = () => {
  const { api, go } = useApp();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [row, setRow] = useState(null);
  const [cfg, setCfg] = useState(DEFAULTS);
  const [isactive, setIsactive] = useState(true);
  const [apikey, setApikey] = useState("");
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState(null);
  const [testName, setTestName] = useState("");
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState(null);
  const [kws, setKws] = useState(null);
  const [kwErr, setKwErr] = useState(null);
  const [kwBusy, setKwBusy] = useState(false);
  const [term, setTerm] = useState("");
  const [sev, setSev] = useState("Medium");

  const loadKws = () => api.get("getNewsSubjects")
    .then(d => setKws((d && d.keywords) || []))
    .catch(e => setKwErr(e.message));
  const saveKw = async (keyword, done) => {
    setKwBusy(true); setKwErr(null);
    try { await api.post("saveNewsSubject", { keyword }); if (done) done(); await loadKws(); }
    catch (e) { setKwErr(e.message); }
    setKwBusy(false);
  };

  const load = () => {
    setLoading(true); setErr(null);
    api.get("getIntegrations", { provider: PROVIDER })
      .then(d => {
        setRow(d || null);
        setCfg({ ...DEFAULTS, ...((d && d.config) || {}) });
        // A new row starts off (no key yet); default the box to on so the first Save turns it on.
        setIsactive(d && d.secretstatus && d.secretstatus[SECRET_APIKEY] ? !!d.isactive : true);
        setLoading(false);
      })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); loadKws(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const haveKey = !!(row && row.secretstatus && row.secretstatus[SECRET_APIKEY]);
  const setC = k => v => setCfg(c => ({ ...c, [k]: v }));
  const look = Number(cfg.lookbackdays), due = Number(cfg.duehours);
  const invalid = !(look >= 1 && look <= 365) ? "Look-back must be 1 to 365 days."
    : !(due >= 6 && due <= 168) ? "Search again after must be 6 to 168 hours."
    : (isactive && !haveKey && !apikey.trim()) ? "Paste the API key before turning it on." : null;

  const save = async () => {
    setSaving(true); setNote(null);
    try {
      const secrets = apikey.trim() ? { [SECRET_APIKEY]: apikey.trim() } : {};
      const r = await api.post("saveIntegration", {
        provider: PROVIDER, displayname: "Serper — News Monitor", isactive, secrets,
        config: { lookbackdays: look, duehours: due, headlinewordrequired: !!cfg.headlinewordrequired,
                  baseurl: BASE_URL, secretnames: [SECRET_APIKEY] },
      });
      setApikey("");
      setRow(x => ({ ...(x || {}), secretstatus: r.secretstatus || {} }));
      setNote("Saved." + (secrets[SECRET_APIKEY] ? " API key written to Key Vault." : ""));
      load();
    } catch (e) { setNote("Not saved — " + e.message); }
    setSaving(false);
  };

  const runTest = async () => {
    setTesting(true); setTest(null);
    try { setTest(await api.post("runNewsScan", { test: testName })); }
    catch (e) { setTest({ ok: false, error: e.message }); }
    setTesting(false);
  };

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;

  return <>
    <SectionHeader title="News (Serper)" actions={[
      <Btn key="open" onClick={() => go("news-monitor")}>Open News Monitor →</Btn>]} />

    <Card style={{ padding: 16, marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Connection</div>
      <Row k="Provider">Serper.dev — Google News search API (paid, by the search)</Row>
      <Row k="Endpoint"><span style={mono}>POST {BASE_URL}</span>
        <div style={{ fontSize: 11.5, color: "var(--color-text-secondary)" }}>Pinned in code (api/shared/newsSearch.js), not editable here.</div></Row>
      <Row k="API key"><span style={chip(haveKey)}>{haveKey ? "set" : "not set"}</span>
        <span style={{ fontSize: 12, color: "var(--color-text-secondary)", marginLeft: 8 }}>
          Key Vault · <span style={mono}>{SECRET_APIKEY}</span> · sent as the <span style={mono}>X-API-KEY</span> header</span></Row>
      <Row k="Cost">One credit per name per search: once a week, plus any Run Scan or Search now. Balance and usage are on serper.dev → Dashboard.</Row>
      <Row k="Coverage">Google News, US edition, English, 10 stories per search, from the past day, week or month as needed.
        The name is sent without quotes (Serper's free plan refuses quoted searches), so a story counts only when the
        name itself is in its headline or snippet.</Row>
      <Row k="Scheduler">
        <span style={mono}>POST https://ace.aequumcapital.com/api/runNewsScan</span>
        <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
          Header <span style={mono}>x-sync-key</span> = the <span style={mono}>SYNC_SHARED_SECRET</span> app setting (not shown).
          Logic App <span style={mono}>ace-news-monitor-weekly</span>, Mondays 4:10 am Central, five calls in a row (~25 names each) —
          see doc/scheduler-news-monitor.md.</div></Row>
      <Row k="What leaves ACE">The search words only: a name, and for a person the state. Nothing else from the file.</Row>
      <Row k="Last run">{row && row.lastsyncdate
        ? <>{fmtDate(row.lastsyncdate)} {new Date(row.lastsyncdate).toLocaleTimeString()} · {row.lastsyncstatus}</>
        : <span style={{ color: "var(--color-text-secondary)" }}>never</span>}</Row>
    </Card>

    <Card style={{ padding: 16, marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Key and settings</div>
      <div style={{ maxWidth: 760, marginBottom: 6 }}>
        <Field label="Serper API key">
          <input type="password" autoComplete="off" value={apikey} onChange={e => setApikey(e.target.value)} style={inp}
                 placeholder={haveKey ? "•••••••• (leave blank to keep)" : "Paste the API key from serper.dev → API Key"} />
        </Field>
        <div style={hint}>Written to Key Vault on Save and never shown again. Paste a new one here to replace it.</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "0 20px", maxWidth: 760 }}>
        <div>
          <Field label="First search looks back (days)">
            <input type="number" min="1" max="365" value={cfg.lookbackdays} onChange={e => setC("lookbackdays")(e.target.value)} style={inp} />
          </Field>
          <div style={hint}>Later searches start a day before the last one that worked.</div>
        </div>
        <div>
          <Field label="Search a name again after (hours)">
            <input type="number" min="6" max="168" value={cfg.duehours} onChange={e => setC("duehours")(e.target.value)} style={inp} />
          </Field>
          <div style={hint}>160 = weekly (the Monday schedule, with slack for a late run). 20 = daily, if the schedule is ever daily again. Run Scan on News Monitor ignores this and searches everyone.</div>
        </div>
        <div style={{ alignSelf: "center" }}>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, cursor: "pointer", marginBottom: 10 }}>
            <input type="checkbox" checked={!!cfg.headlinewordrequired} onChange={e => setC("headlinewordrequired")(e.target.checked)} style={{ marginTop: 2 }} />
            <span>Grade severity on the headline only <span style={{ color: "var(--color-text-secondary)" }}>(a trouble word only in the snippet leaves the story Low)</span></span>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={isactive} onChange={e => setIsactive(e.target.checked)} />
            Enabled — the weekly search runs
          </label>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
        <Btn variant="primary" disabled={saving || !!invalid} onClick={save}>{saving ? "Saving…" : "Save"}</Btn>
        {invalid && <span style={{ fontSize: 12, color: "#993C1D" }}>{invalid}</span>}
        {note && <span style={{ fontSize: 12.5, color: "var(--color-text-secondary)" }}>{note}</span>}
      </div>
    </Card>

    <Card style={{ padding: 16, marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Trouble words and ignore words</div>
      <div style={{ fontSize: 12.5, color: "var(--color-text-secondary)", marginBottom: 10 }}>
        Every story that names the borrower is filed. Its severity is <b>High</b> if a High word is in its headline or snippet,
        <b>Medium</b> for a Medium word, <b>Low</b> for none (headline only, if that box is ticked above). Low stories show on the
        pages but don't send the email or count on the badge. A story with an <b>Ignore</b> word in its headline or snippet is never filed,
        whatever else it says — use it for things that keep tripping the search ("digital-first", "crypto", another company's name).
        Click a word to switch it off or back on. Changes apply from the next search; stories already filed stay.</div>
      {!kws ? (kwErr ? <div style={{ fontSize: 12.5, color: "#993C1D" }}>{kwErr}</div> : <Spinner />) : <>
        {["High", "Medium", "Ignore"].map(level => (
          <div key={level} style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em",
                           color: "var(--color-text-secondary)", width: 64 }}>{level}</span>
            {!kws.some(k => k.severity === level) && <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>none yet</span>}
            {kws.filter(k => k.severity === level).map(k => (
              <span key={k.id} onClick={() => !kwBusy && saveKw({ id: k.id, isactive: !k.isactive })}
                    title={k.isactive ? "Click to switch off" : "Click to switch back on"}
                    style={{ cursor: "pointer", fontSize: 12, padding: "2px 8px", borderRadius: 4, fontWeight: 600,
                             background: level === "High" ? "#FBE4DD" : level === "Ignore" ? "#F1EFE8" : "#FDF0D5",
                             color: level === "High" ? "#993C1D" : level === "Ignore" ? "#5F5E5A" : "#7A5200",
                             opacity: k.isactive ? 1 : 0.4, textDecoration: k.isactive ? "none" : "line-through" }}>{k.term}</span>))}
          </div>))}
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6, maxWidth: 560 }}>
          <input value={term} onChange={e => setTerm(e.target.value)} placeholder="Add a word or phrase" style={inp} />
          <select value={sev} onChange={e => setSev(e.target.value)} style={{ ...inp, width: "auto" }}>
            <option>Medium</option><option>High</option><option>Ignore</option>
          </select>
          <Btn disabled={kwBusy || term.trim().length < 3} onClick={() => saveKw({ term: term.trim(), severity: sev }, () => setTerm(""))}>Add</Btn>
        </div>
        {kwErr && <div style={{ fontSize: 12, color: "#993C1D", marginTop: 6 }}>{kwErr}</div>}
      </>}
    </Card>

    <Card style={{ padding: 16, marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Test search</div>
      <div style={{ fontSize: 12.5, color: "var(--color-text-secondary)", marginBottom: 10 }}>
        Runs the query a company name would get with the saved key and settings, and shows what comes back — the ones
        that name it are what would be filed, with the severity they'd get. Uses one credit; saves nothing.</div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", maxWidth: 620 }}>
        <input value={testName} onChange={e => setTestName(e.target.value)} placeholder="A company name, e.g. Boeing" style={inp} />
        <Btn disabled={testing || !haveKey || testName.trim().length < 3} onClick={runTest}>{testing ? "Searching…" : "Search"}</Btn>
      </div>
      {!haveKey && <div style={{ fontSize: 12, color: "#993C1D", marginTop: 6 }}>Save the API key first.</div>}
      {test && <div style={{ marginTop: 12, borderLeft: "3px solid " + (test.ok ? "#0F6E56" : "#993C1D"), paddingLeft: 12 }}>
        {test.query && <div style={{ ...mono, color: "var(--color-text-secondary)", marginBottom: 6 }}>{test.query}</div>}
        {test.skipped && <div style={{ fontSize: 13, color: "#7A5200" }}>{test.skipped}</div>}
        {test.error && <div style={{ fontSize: 13, color: "#993C1D" }}>{test.error}</div>}
        {test.ok && <div style={{ fontSize: 13, marginBottom: 6 }}>
          {test.count} stor{test.count === 1 ? "y" : "ies"} in the look-back window · {test.wouldfile} would be filed
          {test.credits != null ? ` · ${test.credits} credit${test.credits === 1 ? "" : "s"} used` : ""}.</div>}
        {(test.sample || []).map((a, i) => <div key={i} style={{ fontSize: 12.5, padding: "4px 0", opacity: a.files ? 1 : 0.55 }}>
          <a href={a.url} target="_blank" rel="noopener noreferrer" style={{ color: B.cyanDark }}>{a.title}</a>
          <span style={{ color: "var(--color-text-secondary)" }}> · {a.source || a.domain}{a.seen ? " · " + fmtDate(a.seen) : ""}</span>
          {!a.named && <span style={{ marginLeft: 6, fontSize: 11, color: "var(--color-text-secondary)" }}>not about this name</span>}
          {a.ignored && <span style={{ marginLeft: 6, fontSize: 11, color: "var(--color-text-secondary)" }}>ignored: {a.ignored}</span>}
          {a.files && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 600,
                                     color: a.severity === "High" ? "#993C1D" : a.severity === "Medium" ? "#7A5200" : "#5F5E5A" }}>
            {a.severity}{a.matched ? ": " + a.matched : ""}</span>}
          {a.snippet && <div style={{ fontSize: 11.5, color: "var(--color-text-secondary)" }}>{a.snippet}</div>}
        </div>)}
      </div>}
    </Card>
  </>;
};
